import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

// ---- Value Conversion Utilities ----
// Normalized [0,1] → Hz (20Hz–20kHz, logarithmic)
function normToHz(n) { return 20 * Math.pow(10, Math.max(0, Math.min(1, n)) * 3); }
// Normalized [0,1] → dB (±15 dB range, 0.5 = 0 dB)
function normToDb(n) { return (Math.max(0, Math.min(1, n)) - 0.5) * 30; }
// Normalized [0,1] → Q factor (0.1–10, logarithmic)
function normToQ(n) { return 0.1 * Math.pow(100, Math.max(0, Math.min(1, n))); }

function formatHz(hz) {
  if (hz >= 1000) return (hz / 1000).toFixed(hz >= 10000 ? 1 : 2).replace(/\.?0+$/, '') + 'k';
  return Math.round(hz) + '';
}

// ---- Biquad Filter Math (Audio EQ Cookbook by R. Bristow-Johnson) ----
const FS = 48000;

function peakingCoeffs(freqHz, gainDb, q) {
  const w0 = 2 * Math.PI * freqHz / FS;
  const A = Math.pow(10, gainDb / 40);
  const alpha = Math.sin(w0) / (2 * Math.max(0.01, q));
  const a0 = 1 + alpha / A;
  return {
    b0: (1 + alpha * A) / a0,
    b1: (-2 * Math.cos(w0)) / a0,
    b2: (1 - alpha * A) / a0,
    a1: (-2 * Math.cos(w0)) / a0,
    a2: (1 - alpha / A) / a0,
  };
}

function hpfCoeffs(freqHz) {
  const w0 = 2 * Math.PI * freqHz / FS;
  const alpha = Math.sin(w0) / (2 * 0.7071); // Butterworth (Q=1/√2)
  const a0 = 1 + alpha;
  return {
    b0: (1 + Math.cos(w0)) / 2 / a0,
    b1: -(1 + Math.cos(w0)) / a0,
    b2: (1 + Math.cos(w0)) / 2 / a0,
    a1: (-2 * Math.cos(w0)) / a0,
    a2: (1 - alpha) / a0,
  };
}

function biquadMagDb({ b0, b1, b2, a1, a2 }, w) {
  const cos1 = Math.cos(w), cos2 = Math.cos(2 * w);
  const sin1 = Math.sin(w), sin2 = Math.sin(2 * w);
  const numRe = b0 + b1 * cos1 + b2 * cos2;
  const numIm = -b1 * sin1 - b2 * sin2;
  const denRe = 1 + a1 * cos1 + a2 * cos2;
  const denIm = -a1 * sin1 - a2 * sin2;
  const numSq = numRe * numRe + numIm * numIm;
  const denSq = denRe * denRe + denIm * denIm;
  return 10 * Math.log10(Math.max(1e-20, numSq / Math.max(1e-20, denSq)));
}

function computeEQCurve(params, numPoints) {
  const result = new Float32Array(numPoints);
  for (let i = 0; i < numPoints; i++) {
    const hz = 20 * Math.pow(10, (i / (numPoints - 1)) * 3);
    const w = 2 * Math.PI * hz / FS;
    let totalDb = 0;
    if (!params.bypass) {
      const hpfHz = normToHz(params.hpfFreq);
      if (hpfHz >= 20) totalDb += biquadMagDb(hpfCoeffs(hpfHz), w);
      for (const band of params.bands) {
        const freqHz = normToHz(band.freq);
        const gainDb = normToDb(band.gain);
        const q = normToQ(band.q);
        totalDb += biquadMagDb(peakingCoeffs(freqHz, gainDb, q), w);
      }
    }
    result[i] = totalDb;
  }
  return result;
}

// ---- EQ Canvas Component ----
function EQCanvas({ params }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = canvas.width;
    const H = canvas.height;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#0d0d0d';
    ctx.fillRect(0, 0, W, H);

    const DB_MIN = -18, DB_MAX = 18;
    const freqToX = (hz) => Math.log10(hz / 20) / 3 * W;
    const dbToY = (db) => H * (1 - (db - DB_MIN) / (DB_MAX - DB_MIN));

    // Grid lines
    const freqMarkers = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
    const dbMarkers = [-15, -12, -9, -6, -3, 0, 3, 6, 9, 12, 15];

    freqMarkers.forEach((hz) => {
      const x = freqToX(hz);
      ctx.strokeStyle = '#1e1e1e';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      ctx.fillStyle = '#444';
      ctx.font = '10px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(formatHz(hz), x, H - 2);
    });

    dbMarkers.forEach((db) => {
      const y = dbToY(db);
      ctx.strokeStyle = db === 0 ? '#383838' : '#1e1e1e';
      ctx.lineWidth = db === 0 ? 1.5 : 1;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      if (db !== DB_MIN && db !== DB_MAX) {
        ctx.fillStyle = db === 0 ? '#666' : '#393939';
        ctx.font = '9px monospace';
        ctx.textAlign = 'left';
        ctx.fillText((db > 0 ? '+' : '') + db + 'dB', 2, y - 2);
      }
    });

    // Compute and draw curve
    const NUM_PTS = W;
    const curve = computeEQCurve(params, NUM_PTS);
    const zeroY = dbToY(0);

    ctx.beginPath();
    for (let i = 0; i < NUM_PTS; i++) {
      const y = dbToY(Math.max(DB_MIN, Math.min(DB_MAX, curve[i])));
      if (i === 0) ctx.moveTo(i, y); else ctx.lineTo(i, y);
    }
    // Fill
    ctx.lineTo(W, zeroY);
    ctx.lineTo(0, zeroY);
    ctx.closePath();
    ctx.fillStyle = params.bypass ? 'rgba(120,120,120,0.07)' : 'rgba(240,192,64,0.12)';
    ctx.fill();

    // Stroke the curve on top
    ctx.beginPath();
    for (let i = 0; i < NUM_PTS; i++) {
      const y = dbToY(Math.max(DB_MIN, Math.min(DB_MAX, curve[i])));
      if (i === 0) ctx.moveTo(i, y); else ctx.lineTo(i, y);
    }
    ctx.strokeStyle = params.bypass ? '#555' : '#f0c040';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [params]);

  return (
    <canvas
      ref={canvasRef}
      width={560}
      height={180}
      style={{ width: '100%', height: '180px', display: 'block', borderRadius: '4px', border: '1px solid #2a2a2a' }}
    />
  );
}

// ---- Band Row Component ----
function BandRow({ label, color, freqNorm, gainNorm, qNorm, hasGain, hasQ, onFreq, onGain, onQ }) {
  const freqHz = normToHz(freqNorm);
  const gainDb = normToDb(gainNorm);
  const qVal = normToQ(qNorm);

  return (
    <div className="eq-band">
      <div className="eq-band-label" style={{ color }}>{label}</div>
      <div className="eq-band-param">
        <span className="eq-param-name">FREQ</span>
        <input
          type="range" min={0} max={1} step={0.001}
          value={freqNorm}
          onChange={(e) => onFreq(parseFloat(e.target.value))}
          className="eq-slider"
        />
        <span className="eq-value">{formatHz(freqHz)}</span>
      </div>
      {hasGain && (
        <div className="eq-band-param">
          <span className="eq-param-name">GAIN</span>
          <input
            type="range" min={0} max={1} step={0.001}
            value={gainNorm}
            onChange={(e) => onGain(parseFloat(e.target.value))}
            className="eq-slider eq-slider-gain"
          />
          <span className="eq-value">{(gainDb >= 0 ? '+' : '') + gainDb.toFixed(1) + 'dB'}</span>
        </div>
      )}
      {hasQ && (
        <div className="eq-band-param">
          <span className="eq-param-name">Q</span>
          <input
            type="range" min={0} max={1} step={0.001}
            value={qNorm}
            onChange={(e) => onQ(parseFloat(e.target.value))}
            className="eq-slider eq-slider-q"
          />
          <span className="eq-value">Q:{qVal.toFixed(2)}</span>
        </div>
      )}
    </div>
  );
}

// ---- localStorage Helpers ----
const LS_KEY = 'uisnapshot_eq_presets';
function loadPresets() {
  try { const r = localStorage.getItem(LS_KEY); return r ? JSON.parse(r) : []; } catch { return []; }
}
function persistPresets(p) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

// ---- Main EQ Dialog Component ----
export default function EQDialog({ open, onClose, read, write, channelName }) {
  const pf = (key, fb) => { const v = parseFloat(read(key)); return isNaN(v) ? fb : v; };

  const [bypass, setBypass] = useState(false);
  const [easy, setEasy] = useState(false);
  const [prname, setPrname] = useState('');

  const [hpfFreq, setHpfFreq] = useState(0.1);
  const [b1freq, setB1freq] = useState(0.3); const [b1gain, setB1gain] = useState(0.5); const [b1q, setB1q] = useState(0.5);
  const [b2freq, setB2freq] = useState(0.4); const [b2gain, setB2gain] = useState(0.5); const [b2q, setB2q] = useState(0.5);
  const [b3freq, setB3freq] = useState(0.6); const [b3gain, setB3gain] = useState(0.5); const [b3q, setB3q] = useState(0.5);
  const [b4freq, setB4freq] = useState(0.75); const [b4gain, setB4gain] = useState(0.5); const [b4q, setB4q] = useState(0.5);
  const [b5freq, setB5freq] = useState(0.9); const [b5gain, setB5gain] = useState(0.5); const [b5q, setB5q] = useState(0.5);

  const [presets, setPresets] = useState(loadPresets);
  const [selectedPreset, setSelectedPreset] = useState('');

  // Load values from snapshot when dialog opens
  useEffect(() => {
    if (!open) return;
    setBypass(String(read('eq.bypass')) === '1');
    setEasy(String(read('eq.easy')) === '1');
    const name = read('eq.prname') || '';
    setPrname(name);
    setSelectedPreset(name);

    setHpfFreq(pf('eq.hpf.freq', 0.1));
    setB1freq(pf('eq.b1.freq', 0.3)); setB1gain(pf('eq.b1.gain', 0.5)); setB1q(pf('eq.b1.q', 0.5));
    setB2freq(pf('eq.b2.freq', 0.4)); setB2gain(pf('eq.b2.gain', 0.5)); setB2q(pf('eq.b2.q', 0.5));
    setB3freq(pf('eq.b3.freq', 0.6)); setB3gain(pf('eq.b3.gain', 0.5)); setB3q(pf('eq.b3.q', 0.5));
    setB4freq(pf('eq.b4.freq', 0.75)); setB4gain(pf('eq.b4.gain', 0.5)); setB4q(pf('eq.b4.q', 0.5));
    setB5freq(pf('eq.b5.freq', 0.9)); setB5gain(pf('eq.b5.gain', 0.5)); setB5q(pf('eq.b5.q', 0.5));
  }, [open]); // intentionally only re-run when dialog opens/closes

  // Clear preset name when a parameter is changed manually
  const writeParam = useCallback((key, value) => {
    write(key, value);
    write('eq.prname', '');
    setPrname('');
    setSelectedPreset('');
  }, [write]);

  const eqParams = {
    bypass,
    hpfFreq,
    bands: [
      { freq: b1freq, gain: b1gain, q: b1q },
      { freq: b2freq, gain: b2gain, q: b2q },
      { freq: b3freq, gain: b3gain, q: b3q },
      { freq: b4freq, gain: b4gain, q: b4q },
      { freq: b5freq, gain: b5gain, q: b5q },
    ],
  };

  // ---- Preset helpers ----
  function applyPresetParams(p, presetName) {
    const parseF = (key, fb) => { const v = parseFloat(p[key]); return isNaN(v) ? fb : v; };
    const newBypass = String(p['eq.bypass']) === '1';
    const newEasy = String(p['eq.easy']) === '1';

    setBypass(newBypass); write('eq.bypass', newBypass ? 1 : 0);
    setEasy(newEasy); write('eq.easy', newEasy ? 1 : 0);

    const hp = parseF('eq.hpf.freq', 0.1);
    setHpfFreq(hp); write('eq.hpf.freq', hp);

    const nf1 = parseF('eq.b1.freq', 0.3), ng1 = parseF('eq.b1.gain', 0.5), nq1 = parseF('eq.b1.q', 0.5);
    setB1freq(nf1); write('eq.b1.freq', nf1); setB1gain(ng1); write('eq.b1.gain', ng1); setB1q(nq1); write('eq.b1.q', nq1);

    const nf2 = parseF('eq.b2.freq', 0.4), ng2 = parseF('eq.b2.gain', 0.5), nq2 = parseF('eq.b2.q', 0.5);
    setB2freq(nf2); write('eq.b2.freq', nf2); setB2gain(ng2); write('eq.b2.gain', ng2); setB2q(nq2); write('eq.b2.q', nq2);

    const nf3 = parseF('eq.b3.freq', 0.6), ng3 = parseF('eq.b3.gain', 0.5), nq3 = parseF('eq.b3.q', 0.5);
    setB3freq(nf3); write('eq.b3.freq', nf3); setB3gain(ng3); write('eq.b3.gain', ng3); setB3q(nq3); write('eq.b3.q', nq3);

    const nf4 = parseF('eq.b4.freq', 0.75), ng4 = parseF('eq.b4.gain', 0.5), nq4 = parseF('eq.b4.q', 0.5);
    setB4freq(nf4); write('eq.b4.freq', nf4); setB4gain(ng4); write('eq.b4.gain', ng4); setB4q(nq4); write('eq.b4.q', nq4);

    const nf5 = parseF('eq.b5.freq', 0.9), ng5 = parseF('eq.b5.gain', 0.5), nq5 = parseF('eq.b5.q', 0.5);
    setB5freq(nf5); write('eq.b5.freq', nf5); setB5gain(ng5); write('eq.b5.gain', ng5); setB5q(nq5); write('eq.b5.q', nq5);

    write('eq.prname', presetName);
    setPrname(presetName);
    setSelectedPreset(presetName);
  }

  function handlePresetSelect(name) {
    setSelectedPreset(name);
    if (!name) return;
    const preset = presets.find((p) => p.name === name);
    if (preset) applyPresetParams(preset.params, name);
  }

  function handleSavePreset() {
    const name = window.prompt('Enter preset name:', prname || '');
    if (!name) return;
    const preset = {
      name,
      params: {
        'eq.bypass': bypass ? '1' : '0',
        'eq.easy': easy ? '1' : '0',
        'eq.hpf.freq': String(hpfFreq),
        'eq.b1.freq': String(b1freq), 'eq.b1.gain': String(b1gain), 'eq.b1.q': String(b1q),
        'eq.b2.freq': String(b2freq), 'eq.b2.gain': String(b2gain), 'eq.b2.q': String(b2q),
        'eq.b3.freq': String(b3freq), 'eq.b3.gain': String(b3gain), 'eq.b3.q': String(b3q),
        'eq.b4.freq': String(b4freq), 'eq.b4.gain': String(b4gain), 'eq.b4.q': String(b4q),
        'eq.b5.freq': String(b5freq), 'eq.b5.gain': String(b5gain), 'eq.b5.q': String(b5q),
      },
    };
    const updated = [...presets.filter((p) => p.name !== name), preset];
    setPresets(updated);
    persistPresets(updated);
    write('eq.prname', name);
    setPrname(name);
    setSelectedPreset(name);
  }

  function handleExportPresets() {
    const blob = new Blob([JSON.stringify(presets, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'eq-presets.json'; a.click();
    URL.revokeObjectURL(url);
  }

  function handleImportPresets() {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const imported = JSON.parse(ev.target.result);
          if (!Array.isArray(imported)) throw new Error('not array');
          const merged = [...presets];
          let count = 0;
          for (const p of imported) {
            if (p && p.name && p.params) {
              const idx = merged.findIndex((x) => x.name === p.name);
              if (idx >= 0) merged[idx] = p; else merged.push(p);
              count++;
            }
          }
          setPresets(merged); persistPresets(merged);
          window.alert('Imported ' + count + ' preset(s).');
        } catch { window.alert('Failed to import: expected a JSON array of preset objects with name and params fields.'); }
      };
      reader.readAsText(file);
    };
    input.click();
  }

  if (!open) return null;

  const BAND_COLORS = ['#f0c040', '#7ec8e3', '#a0d8a0', '#e8a0e8', '#f0a070'];

  return ReactDOM.createPortal(
    <div className="eq-overlay" onClick={onClose}>
      <div className="eq-dialog" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="eq-header">
          <span className="eq-title">EQ — {channelName || 'Channel'}</span>
          <div className="eq-header-right">
            <label className="eq-toggle">
              <input
                type="checkbox" checked={bypass}
                onChange={(e) => { const v = e.target.checked; setBypass(v); write('eq.bypass', v ? 1 : 0); }}
              />
              <span>BYPASS</span>
            </label>
            <label className="eq-toggle">
              <input
                type="checkbox" checked={easy}
                onChange={(e) => { const v = e.target.checked; setEasy(v); write('eq.easy', v ? 1 : 0); }}
              />
              <span>EASY</span>
            </label>
            {prname && <span className="eq-active-preset" title={'Preset: ' + prname}>⬡ {prname}</span>}
            <button className="eq-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Frequency response canvas */}
        <div className="eq-canvas-wrap">
          <EQCanvas params={eqParams} />
        </div>

        {/* Band controls */}
        <div className="eq-bands">
          <BandRow
            label="HPF" color="#7ec8e3"
            freqNorm={hpfFreq} gainNorm={0.5} qNorm={0.5}
            hasGain={false} hasQ={false}
            onFreq={(v) => { setHpfFreq(v); writeParam('eq.hpf.freq', v); }}
            onGain={() => {}} onQ={() => {}}
          />
          <BandRow
            label="B1" color={BAND_COLORS[0]}
            freqNorm={b1freq} gainNorm={b1gain} qNorm={b1q}
            hasGain hasQ
            onFreq={(v) => { setB1freq(v); writeParam('eq.b1.freq', v); }}
            onGain={(v) => { setB1gain(v); writeParam('eq.b1.gain', v); }}
            onQ={(v) => { setB1q(v); writeParam('eq.b1.q', v); }}
          />
          <BandRow
            label="B2" color={BAND_COLORS[1]}
            freqNorm={b2freq} gainNorm={b2gain} qNorm={b2q}
            hasGain hasQ
            onFreq={(v) => { setB2freq(v); writeParam('eq.b2.freq', v); }}
            onGain={(v) => { setB2gain(v); writeParam('eq.b2.gain', v); }}
            onQ={(v) => { setB2q(v); writeParam('eq.b2.q', v); }}
          />
          <BandRow
            label="B3" color={BAND_COLORS[2]}
            freqNorm={b3freq} gainNorm={b3gain} qNorm={b3q}
            hasGain hasQ
            onFreq={(v) => { setB3freq(v); writeParam('eq.b3.freq', v); }}
            onGain={(v) => { setB3gain(v); writeParam('eq.b3.gain', v); }}
            onQ={(v) => { setB3q(v); writeParam('eq.b3.q', v); }}
          />
          <BandRow
            label="B4" color={BAND_COLORS[3]}
            freqNorm={b4freq} gainNorm={b4gain} qNorm={b4q}
            hasGain hasQ
            onFreq={(v) => { setB4freq(v); writeParam('eq.b4.freq', v); }}
            onGain={(v) => { setB4gain(v); writeParam('eq.b4.gain', v); }}
            onQ={(v) => { setB4q(v); writeParam('eq.b4.q', v); }}
          />
          <BandRow
            label="B5" color={BAND_COLORS[4]}
            freqNorm={b5freq} gainNorm={b5gain} qNorm={b5q}
            hasGain hasQ
            onFreq={(v) => { setB5freq(v); writeParam('eq.b5.freq', v); }}
            onGain={(v) => { setB5gain(v); writeParam('eq.b5.gain', v); }}
            onQ={(v) => { setB5q(v); writeParam('eq.b5.q', v); }}
          />
        </div>

        {/* Preset bar */}
        <div className="eq-preset-bar">
          <select
            className="eq-preset-select"
            value={selectedPreset}
            onChange={(e) => handlePresetSelect(e.target.value)}
          >
            <option value="">— Select Preset —</option>
            {presets.map((p) => (
              <option key={p.name} value={p.name}>{p.name}</option>
            ))}
          </select>
          <button className="eq-btn" onClick={handleSavePreset}>SAVE</button>
          <button className="eq-btn" onClick={handleExportPresets}>EXPORT</button>
          <button className="eq-btn" onClick={handleImportPresets}>IMPORT</button>
        </div>

      </div>
    </div>,
    document.body
  );
}
